import React, { Component } from 'react';
import { connect } from 'react-redux';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import { loadUsageReport, downloadReport, refreshUsageReport } from '../../data-dictionary-view.actions';
import ReportShell from './report-shell';

const WINDOW_OPTIONS = [
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
];

class UsageAccessReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      window: '30d',
      schema: '',
      dormantOnly: false,
      hotOnly: false,
      search: '',
    };
  }

  componentDidMount() {
    this.loadReport();
  }

  loadReport = () => {
    const { window, schema, dormantOnly, hotOnly, search } = this.state;
    this.props.loadUsageReport({
      window,
      schema,
      dormantOnly: dormantOnly ? 'true' : '',
      hotOnly: hotOnly ? 'true' : '',
      search,
    });
  }

  handleFilterChange = (field) => (e) => {
    this.setState({ [field]: e.target.value }, this.loadReport);
  }

  handleCheckboxChange = (field) => (e) => {
    this.setState({ [field]: e.target.checked }, this.loadReport);
  }

  handleRefresh = () => {
    const { window } = this.state;
    this.props.refreshUsageReport({ window });
    setTimeout(() => this.loadReport(), 3000);
  }

  handleDownload = () => {
    const { window, schema, dormantOnly, hotOnly, search } = this.state;
    this.props.downloadReport('usage', {
      window,
      schema,
      dormantOnly: dormantOnly ? 'true' : '',
      hotOnly: hotOnly ? 'true' : '',
      search,
    });
  }

  handleCardClick = (key) => {
    if (key === 'total') {
      this.setState({ dormantOnly: false, hotOnly: false }, this.loadReport);
    } else if (key === 'dormant') {
      this.setState({ dormantOnly: !this.state.dormantOnly, hotOnly: false }, this.loadReport);
    } else {
      // Active - clear dormantOnly
      this.setState({ dormantOnly: false, hotOnly: false }, this.loadReport);
    }
  }

  getSummaryTiles(data) {
    const { active, dormant } = data.reduce((acc, r) => {
      if ((r.status || '').toLowerCase() === 'dormant') acc.dormant++;
      else acc.active++;
      return acc;
    }, { active: 0, dormant: 0 });
    const { dormantOnly } = this.state;
    return [
      { label: 'Total', value: data.length, className: 'total', key: 'total', isActive: !dormantOnly, onClick: () => this.handleCardClick('total') },
      { label: 'Active', value: active, className: 'pending', key: 'active', isActive: false, onClick: () => this.handleCardClick('active') },
      { label: 'Dormant', value: dormant, className: 'ignored', key: 'dormant', isActive: dormantOnly, onClick: () => this.handleCardClick('dormant') },
    ];
  }

  renderFilters() {
    const { window, schema, dormantOnly, hotOnly, search } = this.state;
    const { schemas } = this.props;
    return (
      <div className="dd-report-filter-row-wrap">
        <div className="dd-view-filter-row">
          <TextField
            select
            label="Window"
            value={window}
            onChange={this.handleFilterChange('window')}
            variant="standard"
            fullWidth
          >
            {WINDOW_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Schema"
            value={schema}
            onChange={this.handleFilterChange('schema')}
            variant="standard"
            fullWidth
          >
            <MenuItem value="">All</MenuItem>
            {Array.isArray(schemas) && schemas.map(s => (
              <MenuItem key={s.schemaId} value={s.schemaId}>{s.instanceName} ({s.schemaName})</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Search"
            value={search}
            onChange={this.handleFilterChange('search')}
            variant="standard"
            fullWidth
            InputProps={{
              endAdornment: search && (
                <InputAdornment position="end">
                  <i className="material-icons search-clear" onClick={() => this.setState({ search: '' }, this.loadReport)}>close</i>
                </InputAdornment>
              )
            }}
          />
        </div>
        <div className="dd-report-checkbox-row">
          <FormControlLabel
            control={<Checkbox checked={dormantOnly} onChange={this.handleCheckboxChange('dormantOnly')} color="primary" />}
            label="Dormant only (>90d)"
          />
          <FormControlLabel
            control={<Checkbox checked={hotOnly} onChange={this.handleCheckboxChange('hotOnly')} color="primary" />}
            label="Hot tables (top 10%)"
          />
        </div>
      </div>
    );
  }

  renderTable(data) {
    return (
      <div className="object-table-container">
        <table className="object-table">
          <thead>
            <tr>
              <th>Schema</th>
              <th>Table</th>
              <th>Last Queried</th>
              <th>Queries (window)</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                <td>{row.schema}</td>
                <td>{row.table}</td>
                <td>{row.lastQueried || <span className="dd-no-value">Never</span>}</td>
                <td>{row.queryCount ?? <span className="dd-no-value">—</span>}</td>
                <td>
                  <span className={`dd-badge badge-${(row.status || 'ok').toLowerCase()}`}>
                    {row.status || 'OK'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const { usageReport, onBack } = this.props;
    const data = usageReport.data || [];
    const lastRefreshedAt = usageReport.lastRefreshedAt;
    const refreshedBy = usageReport.refreshedBy;
    const isRefreshing = usageReport.isRefreshing;

    const refreshInfo = lastRefreshedAt
      ? `Last refreshed: ${new Date(lastRefreshedAt).toLocaleString()}${refreshedBy ? ` by ${refreshedBy}` : ''}`
      : 'Not yet refreshed';

    const extraActions = (
      <div className="dd-usage-refresh-bar">
        <span className="dd-usage-refresh-info">{refreshInfo}</span>
        <Button
          variant="outlined"
          size="small"
          onClick={this.handleRefresh}
          disabled={isRefreshing}
          startIcon={isRefreshing ? <CircularProgress size={14} /> : <i className="material-icons">refresh</i>}
        >
          {isRefreshing ? 'Refreshing…' : 'Refresh usage data'}
        </Button>
      </div>
    );

    return (
      <ReportShell
        title="Usage / Access Report"
        icon="bar_chart"
        summaryTiles={this.getSummaryTiles(data)}
        filters={this.renderFilters()}
        table={this.renderTable(data)}
        isLoading={usageReport.isLoading}
        error={usageReport.error}
        data={data}
        onBack={onBack}
        onDownload={this.handleDownload}
        extraActions={extraActions}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  usageReport: state.dataDictionaryViewReducer.reports.usage,
  schemas: state.dataDictionaryViewReducer.schemas,
});

export default connect(mapStateToProps, { loadUsageReport, downloadReport, refreshUsageReport })(UsageAccessReport);
